import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as ReportingActions from '@reporting/reporting.actions';
import * as fromReporting from '@reporting/reporting.reducers';

import { emptySpacesValidator } from '@app/validators/empty-spaces-validator';
import { SuperhumanAnswer, SuperhumanPutAnswer, SuperhumanQuestion } from '@shared/models/Superhuman';
import { ReportingService } from '@shared/services/reporting.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
  standalone: false
})
export class SurveyComponent implements OnInit, OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  form: FormGroup;
  initialFormValue = {};
  questionNumber = 1;
  exitModal = false;
  questions: SuperhumanQuestion[];
  savedAnswers = [];
  submitted: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private store: Store,
    private trackingService: TrackingService,
    private reportingService: ReportingService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({});

    this.store.pipe(select(fromReporting.selectSuperhuman), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.exitModal = value.showModal;
      if (!value.survey.length) {
        return;
      }

      this.questions = value.survey;
      this.setValidators(this.questions);
    });
  }

  ngOnDestroy() {
    if (this.submitted) {
      this.store.dispatch(ReportingActions.getCustomerNotifications());
    }
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setValidators(questions: SuperhumanQuestion[]): void {
    questions.forEach((question) => {
      if (question.type === 'singlechoice') {
        this.form.addControl(`q${question.inyova_id}`, new FormControl('', Validators.required));
      }

      if (question.type === 'open') {
        this.form.addControl(
          `q${question.inyova_id}`,
          new FormControl('', Validators.compose([Validators.required, Validators.minLength(3), emptySpacesValidator]))
        );
      }

      if (question.type === 'multichoice') {
        this.form.addControl(`q${question.inyova_id}`, this.formBuilder.array([], [Validators.required]));

        const questionController = this.form.get(`q${question.inyova_id}`) as FormArray;
        if (question.survey_customer_answer) {
          ((question.survey_customer_answer as SuperhumanAnswer).answer as []).forEach(() => {
            questionController.push(new FormControl(''));
          });
        }
      }

      if (question.type === 'rating') {
        this.form.addControl(`q${question.inyova_id}`, new FormControl(null, Validators.required));
      }

      // save initial value if the question has answers
      if (question.survey_customer_answer) {
        if (Array.isArray(question.survey_customer_answer['answer'])) {
          const multipleAnswers = (question.survey_customer_answer as SuperhumanAnswer[])['answer'];
          this.form.get(`q${question.inyova_id}`).patchValue(multipleAnswers);
        } else {
          this.form.get(`q${question.inyova_id}`).setValue((question.survey_customer_answer as SuperhumanAnswer).answer);
        }
      }
    });

    this.initialFormValue = this.form.getRawValue();
    this.savedAnswers = this.fillInSurveyData();
    this.questionNumber = this.savedAnswers.length || 1;
  }

  prev(index: number): void {
    if (!this.savedAnswers[index - 1]) {
      return;
    }
    this.questionNumber = index;
  }

  next(): void {
    this.questionNumber += 1;
    this.save();
  }

  save(): void {
    const wasChanged = JSON.stringify(this.form.value) !== JSON.stringify(this.initialFormValue);

    if (!wasChanged && this.questionNumber < this.questions.length) {
      return;
    }

    this.savedAnswers = this.fillInSurveyData();
    this.submitted = this.questionNumber > this.questions.length;
    const survey = {
      answers: this.savedAnswers,
      answered_from: 'web-app',
      submitted: this.submitted
    };

    this.reportingService.saveSuperHumanSurvey(survey).subscribe((response) => {
      if (response.success) {
        this.trackCTA(`Superhuman Survey: [Button] Question ${this.questionNumber} answered`);

        // update inital with latest one
        this.initialFormValue = this.form.getRawValue();

        if (this.submitted) {
          this.trackCTA('Superhuman Survey: Successfully submited');
        }
      }
    });
  }

  fillInSurveyData(): SuperhumanPutAnswer[] {
    const answers: SuperhumanPutAnswer[] = [];
    this.questions.forEach((question) => {
      const { value }: { value: string | [] } = this.form.controls[`q${question.inyova_id}`];
      if (value === null || value.length === 0 || (Array.isArray(value) && !value.length)) {
        return;
      }

      answers.push({
        question_id: question.id,
        answer: value
      });
    });

    return answers;
  }

  finish(): void {
    this.trackCTA('Superhuman Survey: [Button] Back to reporting');
    this.store.dispatch(ReportingActions.updateSuperhumanExitSurvey({ value: true }));
    this.store.dispatch(ReportingActions.getCustomerNotifications());
    void this.router.navigate(['reports', 'dashboard']);
  }

  trackCTA(action: string): void {
    this.trackingService.trackActivity(action, true);
  }

  onSelectChange(questionID: string, answer: string): void {
    const multichoice: FormArray = this.form.get(`q${questionID}`) as FormArray;
    if ((multichoice.value as string[]).indexOf(answer) === -1) {
      multichoice.push(new FormControl(answer));
    } else {
      multichoice.controls.forEach((item: FormControl, index: number) => {
        if (item.value === answer) {
          multichoice.removeAt(index);
        }
      });
    }
  }

  inList(questionID: string, element: string): boolean {
    return (this.form.get(`q${questionID}`).value as string[]).indexOf(element) !== -1;
  }
}
